import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/App.css";
import "@/Auth.css";
import { api } from "@/api";
import { AuthContext } from "@/auth";
import Login from "@/pages/Login";
import Selector from "@/pages/Selector";
import Settings from "@/pages/Settings";
import CompanyDetails from "@/pages/CompanyDetails";
import SignAffidavit from "@/pages/SignAffidavit";

// Owner's rule: every page opens at the very top (no remembered scroll position)
if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

function ScrollToTop({ signedIn }) {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, signedIn]);
  return null;
}

const PRINT_BLOCKED =
  "Printing this website is disabled. Sign the affidavit and click Print to PDF and Send: the signed affidavit is emailed as a PDF.";

// Owner (as on the debit order sites): the website's pages can't be printed - Ctrl/Cmd+P is blocked,
// and printing from the browser menu or right-click prints only a notice (@media print in App.css).
function PrintBlocker() {
  const [notice, setNotice] = useState(false);
  useEffect(() => {
    const blockShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        e.stopPropagation();
        setNotice(true);
      }
    };
    const beforePrint = () => setNotice(true);
    window.addEventListener("keydown", blockShortcut, true);
    window.addEventListener("beforeprint", beforePrint);
    return () => {
      window.removeEventListener("keydown", blockShortcut, true);
      window.removeEventListener("beforeprint", beforePrint);
    };
  }, []);
  if (!notice) return null;
  return (
    <div className="modal-overlay" data-testid="print-blocked-dialog" onClick={() => setNotice(false)}>
      <div className="modal-content bee-problem-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Printing Disabled</h2>
        <p className="modal-description">{PRINT_BLOCKED}</p>
        <div className="modal-actions">
          <button type="button" className="send-btn" onClick={() => setNotice(false)} data-testid="button-print-blocked-ok">OK</button>
        </div>
      </div>
    </div>
  );
}

// Owner (2026-10-10): signing in always opens the affidavits front page - not the page the user was on
// when they signed out (e.g. Settings). A page reload while signed in stays where it is.
function SignIn({ onSignedIn }) {
  const navigate = useNavigate();
  return (
    <Login
      onSignedIn={(u) => {
        navigate("/", { replace: true });
        onSignedIn(u);
      }}
    />
  );
}

function App() {
  // undefined while checking the session, null when signed out
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api("/auth/me")
      .then(setUser)
      .catch(() => {
        // Signed out / session ended (e.g. a page opened again after 8 hours): the sign-in page shows on
        // the front address (owner, 2026-10-10). The router isn't running yet, so the address is just replaced.
        if (window.location.pathname !== "/") window.history.replaceState(null, "", "/");
        setUser(null);
      });
  }, []);

  // Owner (2026-10-10): signing out reloads the website's front address (not the page the user was on,
  // e.g. /settings); replace, so Back doesn't return to the signed-in page
  const signOut = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {
      // signed out anyway as far as this page is concerned; the reload checks the session again
    }
    window.location.replace("/");
  }, []);

  // The session ended while using the site (8 hours, or the administrator changed the account): a fresh
  // reload of the front address, as signing out (owner, 2026-10-10)
  const expired = useCallback(() => window.location.replace("/"), []);
  // setUser: Settings → Edit User on your own account updates the name shown in the account bars
  const auth = useMemo(() => ({ user, setUser, signOut, expired }), [user, signOut, expired]);

  if (user === undefined) {
    return (
      <div className="bee-boot">
        <Loader2 size={30} className="bee-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
      <BrowserRouter>
        <PrintBlocker />
        <ScrollToTop signedIn={!!user} />
        {/* Invitation only: every page needs a signed-in user (the sign-in page shows on any address) */}
        {!user ? (
          <SignIn onSignedIn={setUser} />
        ) : (
          <Routes>
            <Route path="/" element={<Selector />} />
            <Route path="/affidavit/:slug/details" element={<CompanyDetails />} />
            <Route path="/affidavit/:slug" element={<SignAffidavit />} />
            <Route path="/settings" element={user.role === "admin" ? <Settings /> : <Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
